/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Box, Button, Flex, Table, Thead, Tbody, Tr, Th, Td, Icon, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import DatePicker, { DatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { InputCustom } from '../../UI';
import { OrderStatus } from '../../../../constants/statusConfig';
import OrderStatusBadge from '../../orderStatusBadge';
import { RefundDTO } from '../../../../modules/refunds/types';
import { AttachmentIcon } from '@chakra-ui/icons';

const PAGE_SIZE = 10;

// Стилизованный DatePicker
const StyledDatePicker = styled(DatePicker as unknown as React.ComponentType<DatePickerProps>)`
  width: 150px;
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  &:hover {
    border-color: #cbd5e0;
  }
  &:focus {
    border-color: #3182ce;
    box-shadow: 0 0 0 1px #3182ce;
  }
`;

interface ReturnsContentProps {
  data: RefundDTO[];
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  handleLoadMore: () => void;
  visibleItems: number;
  onSelectReturn: (returnData: any) => void;
}

const ReturnsContent: React.FC<ReturnsContentProps> = ({
  data,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  searchTerm,
  setSearchTerm,
  handleLoadMore,
  visibleItems,
  onSelectReturn,
}) => {
  const displayedData = data.slice(0, visibleItems);

  return (
    <Flex flex="1" direction="column" p={8}>
      <Flex justify="space-between" alignItems="flex-end" mb={6}>
        {/* Поле поиска */}
        <Box width="342px" mb={4} mr={4}>
          <InputCustom
            hasSearchIcon
            placeholder="Поиск по возвратам"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </Box>

        <Flex>
          {/* Стартовый DatePicker */}
          <Box ml="8px">
            <StyledDatePicker
              selected={startDate}
              onChange={(date: Date | null) => setStartDate(date)}
              selectsStart
              startDate={startDate || undefined}
              endDate={endDate || undefined}
              dateFormat="dd.MM.yyyy"
              placeholderText="DD.MM.YYYY"
            />
          </Box>

          {/* Конечный DatePicker */}
          <Box ml="8px">
            <StyledDatePicker
              selected={endDate}
              onChange={(date: Date | null) => setEndDate(date)}
              selectsEnd
              startDate={startDate || undefined}
              endDate={endDate || undefined}
              minDate={startDate || undefined}
              dateFormat="dd.MM.yyyy"
              placeholderText="DD.MM.YYYY"
            />
          </Box>
        </Flex>
      </Flex>

      {/* Таблица данных или сообщение "Нет данных" */}
      {displayedData.length > 0 ? (
        <Box borderWidth="1px" borderRadius="md" overflow="hidden">
          <Table variant="simple">
            <Thead bg="gray.100">
              <Tr>
                <Th>Дата</Th>
                <Th>Заказ</Th>
                <Th>Статус</Th>
                <Th>Товар</Th>
                <Th textAlign="right">Вложения</Th>
              </Tr>
            </Thead>
            <Tbody>
              {displayedData.map((row, index) => (
                <Tr key={index} onClick={() => onSelectReturn(row)} cursor="pointer">
                  <Td>{dayjs(row.created_at).format('DD.MM.YYYY')}</Td>
                  <Td>{row.number}</Td>
                  <Td>
                    <OrderStatusBadge status={row.status as unknown as OrderStatus} />
                  </Td>
                  <Td>{row.order_item.good_name}</Td>
                  <Td style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Icon as={AttachmentIcon} boxSize={6} color="gray.500" />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : (
        <Flex align="center" justify="center" height="200px">
          <Text fontSize="lg" color="gray.500">
            Нет данных
          </Text>
        </Flex>
      )}

      {/* Кнопка для загрузки дополнительных данных */}
      {visibleItems < data.length && (
        <Flex width="100%" justifyContent="center">
          <Button onClick={handleLoadMore} mt={4}>
            Показать следующие {PAGE_SIZE}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default ReturnsContent;
